import {
  Box,
  Button,
  Card,
  Grid,
  Group,
  MultiSelect,
  SegmentedControl,
  Select,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { IconCopy, IconDeviceFloppy, IconMail, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { languagesForTargeting } from '../../../constants/googleLanguges';
import useFetch from '../../../helpers/useFetch';
import ErrorMessage from '../../ErrorMessage';
import LabeledSlider from '../../LabeledSlider';
import LockableButton from '../../Lockable/LockableButton';
import ConfirmationModal from '../../Modals/ConfirmationModal';
import Spinner from '../../Spinner';
import WarningMessageOnDelete from '../../WarningMessageOnDelete';
import GoogleLocationSelector from './GoogleLocationSelector';
const languageOptions = languagesForTargeting.map((language) => ({
  label: language.name,
  value: language.id,
}));

const GoogleAdForm = ({
  googleAd: passedGoogleAd,
  saveGoogleAd,
  deleteGoogleAd,
  onBack,
  onDuplicate,
  showUserFlow,
}) => {
  const fetch = useFetch();
  const [googleAd, setGoogleAd] = useState(passedGoogleAd);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState();
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState();
  const [availableCampaignConversionGoals, setAvailableCampaignConversionGoals] = useState([]);
  const [currentConversionGoals, setCurrentConversionGoals] = useState([]);
  const [goalType, setGoalType] = useState(googleAd?.resourceName ? googleAd.conversionGoalsType : 'account-default');
  const [isGoalSelectEmpty, setIsGoalSelectEmpty] = useState(false);
  const accountDefaultConversionGoals = googleAd.accountDefaultConversionGoals || [];
  const { domain } = useSelector((state) => state.domain);
  const { accountId, domainId } = useParams();

  const googleAccount = useSelector((state) => state.google.account);
  const currency = googleAccount?.adAccountCurrency || domain.currency;
  const symbol = Intl.NumberFormat(navigator.language, {
    style: 'currency',
    currency,
  })
    .formatToParts(1)
    .find((x) => x.type === 'currency').value;

  const customerId = googleAccount?.adAccountId;
  const campaignId = googleAd?.resourceName?.split('/')[3];

  const isLoading = !campaignId && !customerId;

  useEffect(() => {
    if (campaignId && customerId) {
      const fetchData = async () => {
        try {
          const { matchedGoals } = await fetch(
            `/api/admin/accounts/${accountId}/domains/${domainId}/google/${customerId}/ads/${campaignId}/matchedGoalsAndActions`,
          );
          setAvailableCampaignConversionGoals(matchedGoals);
        } catch (error) {
          setLoadError(error);
        }
      };
      fetchData();
    }
  }, [fetch, accountId, domainId, customerId, campaignId]);

  useEffect(() => {
    if (googleAd.conversionGoalsType !== goalType) {
      setGoogleAd((prev) => ({ ...prev, conversionGoalsType: goalType }));
    }
  }, [goalType, googleAd.conversionGoalsType]);

  if (isLoading) {
    return <Spinner />;
  }
  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  const deleteButton = deleteGoogleAd ? (
    <LockableButton restrictionKey="googleAdsDelete" color="red" onClick={() => setConfirmDeleteOpen(true)}>
      <IconTrash size={14} /> Delete
    </LockableButton>
  ) : undefined;

  const duplicateButton =
    googleAd.id && onDuplicate ? (
      <LockableButton restrictionKey="googleAdsCreate" color="cyan" onClick={onDuplicate}>
        <IconCopy size={14} /> Make a copy
      </LockableButton>
    ) : undefined;

  const ProTip = ({ type }) => (
    <Text component="span" size="sm">
      You can optionally direct Google to the preferred bidding strategy and set the {type} for your campaign.
      <strong>Leave blank if unsure</strong>.
      <br />
      Pro Tip: If your account has a lot of past data, you can immediately focus on achieving target CPA or ROAS.
      However, if your account is new, begin with the "Maximize Conversions" setting to allow the system to collect
      data. Once you are consistently receiving conversions, you can set tCPA or tROAS to reach your desired outcomes.
    </Text>
  );

  const goalsToUse =
    goalType === 'campaign-specific'
      ? currentConversionGoals?.length
        ? currentConversionGoals
        : availableCampaignConversionGoals
      : accountDefaultConversionGoals.length
        ? accountDefaultConversionGoals
        : availableCampaignConversionGoals;

  const handleSelectChange = (selectedOptions) => {
    const selectedValues = selectedOptions.map((option) => {
      const value = JSON.parse(option);
      return {
        resourceName: value.resourceName,
        category: value.category,
        origin: value.origin,
        biddable: true,
      };
    });

    handleGoalTypeChange('campaign-specific');
    setIsGoalSelectEmpty(selectedOptions.length === 0);

    const updatedConversionGoals = availableCampaignConversionGoals.map((goal) => ({
      ...goal,
      campaignConversionGoal: {
        ...goal.campaignConversionGoal,
        biddable: selectedValues.some((selected) => selected.resourceName === goal.campaignConversionGoal.resourceName),
      },
    }));
    setCurrentConversionGoals(updatedConversionGoals);
    setGoogleAd({
      ...googleAd,
      conversionGoals: updatedConversionGoals.filter((goal) => goal.campaignConversionGoal.biddable === true),
    });
  };

  const handleGoalTypeChange = (type) => {
    setGoalType(type);
    if (type === 'account-default') {
      setGoogleAd((prev) => ({
        ...prev,
        conversionGoals: accountDefaultConversionGoals,
      }));
    }
  };

  const selectOptions = goalsToUse
    .filter(({ campaignConversionGoal }) => !campaignConversionGoal.biddable)
    .map(({ campaignConversionGoal }) => ({
      key: campaignConversionGoal.resourceName,
      label: `Category: ${campaignConversionGoal.category} // Origin: ${campaignConversionGoal.origin}`,
      value: JSON.stringify({
        resourceName: campaignConversionGoal.resourceName,
        category: campaignConversionGoal.category,
        origin: campaignConversionGoal.origin,
      }),
    }));

  const selectValues = goalsToUse
    .filter(({ campaignConversionGoal }) => campaignConversionGoal.biddable === true)
    .map(({ campaignConversionGoal }) => ({
      key: campaignConversionGoal.resourceName,
      label: `Category: ${campaignConversionGoal.category} // Origin: ${campaignConversionGoal.origin}`,
      value: JSON.stringify({
        resourceName: campaignConversionGoal.resourceName,
        category: campaignConversionGoal.category,
        origin: campaignConversionGoal.origin,
        biddable: true,
      }),
    }));

  return (
    <Card withBorder>
      <Card.Section inheritPadding py="sm">
        <Box component="form" ta="left">
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 3 }}>
              <Text size="sm" fw={500}>
                Name
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 9 }}>
              <TextInput
                name="name"
                type="text"
                value={googleAd?.name || ''}
                placeholder="e.g. Idle Customers"
                onChange={(e) => setGoogleAd({ ...googleAd, [e.target.name]: e.target.value })}
              />
            </Grid.Col>
          </Grid>
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 3 }}>
              <Text size="sm" fw={500}>
                Type
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 9 }}>
              <SegmentedControl
                value={String(googleAd?.isRetail || false)}
                onChange={(value) => setGoogleAd({ ...googleAd, isRetail: value === 'true' })}
                disabled={!!googleAd.id || !googleAccount?.merchantId}
                data={[
                  { label: 'Performance Max', value: 'false' },
                  { label: 'Performance Max Retail', value: 'true' },
                ]}
              />

              <Text size="sm" c="dimmed">
                In order to use Performance Max for retail, the Google Ads account must be linked to an existing
                Merchant Center account.
              </Text>
            </Grid.Col>
          </Grid>
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 3 }}>
              <Text size="sm" fw={500}>
                Budget
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 9 }}>
              <TextInput
                name="budget"
                type="number"
                min={10}
                leftSection={symbol || null}
                value={googleAd?.budget || ''}
                placeholder="e.g. 50"
                onChange={(e) => setGoogleAd({ ...googleAd, [e.target.name]: e.target.value })}
              />
              <Text size="sm" c="dimmed">
                This controls the average daily spend. See{' '}
                <a href="https://support.google.com/google-ads/answer/2375454" target="_lank">
                  this article
                </a>{' '}
                for more information. <br />
                <strong>Note:</strong> this budget is <strong>Not</strong> shared across all campaigns.
              </Text>
            </Grid.Col>
          </Grid>
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 3 }}>
              <Text size="sm" fw={500}>
                Bidding Strategy Type
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 9 }}>
              <Select
                data={[
                  {
                    label: 'Maximize Conversions',
                    value: 'MAXIMIZE_CONVERSIONS',
                  },
                  {
                    label: 'Maximize Conversions Value',
                    value: 'MAXIMIZE_CONVERSION_VALUE',
                  },
                ]}
                onChange={(value) => {
                  setGoogleAd({ ...googleAd, biddingStrategyType: value });
                }}
                value={googleAd?.biddingStrategyType}
              />
              <Text size="sm" c="dimmed">
                The campaign's objective. See{' '}
                <a href="https://www.google.com/business/ads/ad-objectives" target="_blank" rel="noreferrer">
                  this link
                </a>{' '}
                for details.
              </Text>
            </Grid.Col>
          </Grid>
          {googleAd.biddingStrategyType === 'MAXIMIZE_CONVERSIONS' ? (
            <Grid mb="md">
              <Grid.Col span={{ base: 12, sm: 3 }}>
                <Text size="sm" fw={500}>
                  Target Cap (cost-per-action)
                </Text>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 9 }}>
                <TextInput
                  name="targetCpa"
                  type="number"
                  min={0.1}
                  leftSection={symbol || null}
                  value={googleAd.targetCpa || ''}
                  placeholder="e.g. 2"
                  onChange={(e) =>
                    setGoogleAd({
                      ...googleAd,
                      [e.target.name]: e.target.value === '' ? null : e.target.value,
                      targetRoas: null,
                    })
                  }
                />

                <Text size="sm" c="dimmed">
                  <ProTip type={'target CPA'} />
                </Text>
              </Grid.Col>
            </Grid>
          ) : (
            <Grid mb="md">
              <Grid.Col span={{ base: 12, sm: 3 }}>
                <Text size="sm" fw={500}>
                  Target ROAS (return on ad spend)
                </Text>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 9 }}>
                <TextInput
                  name="targetRoas"
                  type="number"
                  min={0.01}
                  value={googleAd.targetRoas || ''}
                  placeholder="e.g. 1.2"
                  onChange={(e) =>
                    setGoogleAd({
                      ...googleAd,
                      [e.target.name]: e.target.value === '' ? null : e.target.value,
                      targetCpa: null,
                    })
                  }
                />
                <Text size="sm" c="dimmed">
                  <ProTip type={'target ROAS'} />
                </Text>
              </Grid.Col>
            </Grid>
          )}
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 3 }}>
              <Text size="sm" fw={500}>
                Duration (Days)
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 9 }}>
              <SegmentedControl
                value={googleAd?.durationInDays ? 'timeFramed' : 'unlimitedTime'}
                onChange={(value) =>
                  setGoogleAd({
                    ...googleAd,
                    durationInDays: value === 'unlimitedTime' ? null : 7,
                  })
                }
                data={[
                  { label: 'Time Framed', value: 'timeFramed' },
                  { label: 'Unlimited Time', value: 'unlimitedTime' },
                ]}
              />
              {googleAd?.durationInDays ? (
                <Box mt="md">
                  <LabeledSlider
                    min={1}
                    max={31}
                    step={1}
                    value={googleAd.durationInDays ? googleAd.durationInDays : 7}
                    onChange={(durationInDays) => setGoogleAd({ ...googleAd, durationInDays })}
                  />
                </Box>
              ) : (
                <Text size="sm" c="dimmed" mt="xs">
                  Your Google Ad will be created without a specific end time.{' '}
                  <strong>Note that your set budget can affect the duration</strong>.
                </Text>
              )}
            </Grid.Col>
          </Grid>
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 3 }}>
              <Text size="sm" fw={500}>
                Languages
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 9 }}>
              <MultiSelect
                searchable={true}
                data={languageOptions}
                onChange={(languages) => {
                  setGoogleAd((prev) => {
                    return {
                      ...prev,
                      languages,
                    };
                  });
                }}
                value={googleAd?.languages}
                placeholder="Select at least one langue..."
              />
              <Text size="sm" c="dimmed">
                Select the languages your customers speak. If no languages are selected, all languages will be used by
                default.
              </Text>
            </Grid.Col>
          </Grid>
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 3 }}>
              <Text size="sm" fw={500}>
                Targeting Locations
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 9 }}>
              <GoogleLocationSelector
                selected={googleAd?.locations}
                handleSelect={(locations) => {
                  setGoogleAd((prev) => {
                    return {
                      ...prev,
                      locations,
                    };
                  });
                }}
              />
              <Text size="sm" c="dimmed">
                Select the locations you want to <strong>TARGET</strong> in your Performance Max campaign. If no
                locations are selected, all countries and territories will be used by default.
              </Text>
            </Grid.Col>
          </Grid>
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 3 }}>
              <Text size="sm" fw={500}>
                URL Expansion
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 9 }}>
              <Switch
                checked={!googleAd?.urlExpansionOptOut}
                onChange={(e) =>
                  setGoogleAd({
                    ...googleAd,
                    urlExpansionOptOut: !e.currentTarget.checked,
                  })
                }
              />

              <Text size="sm" c="dimmed">
                Google Ads automatically expands your ad's reach by using other relevant URLs from your domain beyond
                what you specify in the final URLs
              </Text>
            </Grid.Col>
          </Grid>
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 3 }}>
              <Text size="sm" fw={500}>
                Conversion Goals
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 9 }}>
              <SegmentedControl
                value={goalType ?? ''}
                onChange={(value) => handleGoalTypeChange(value)}
                data={[
                  { label: 'Account default', value: 'account-default' },
                  { label: 'Campaign specific', value: 'campaign-specific' },
                ]}
                disabled={!campaignId}
              />
              {!campaignId && (
                <Text size="sm" c="dimmed">
                  All conversion goals with conversion actions set to account-default at customer/account level will be
                  included in your Google Ad campaign upon creation. You can edit these goals after the campaign is
                  created. For more details, see{' '}
                  <a
                    href="https://developers.google.com/google-ads/api/videos/catalog/pmax-8"
                    target="_blank"
                    rel="noreferrer"
                  >
                    this link
                  </a>
                  .
                </Text>
              )}
            </Grid.Col>
          </Grid>
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 3 }}>
              <Text size="sm" fw={500}>
                &nbsp;
              </Text>
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 9 }}>
              {campaignId && (
                <>
                  <Select
                    className={isGoalSelectEmpty && goalType !== 'account-default' ? 'is-invalid' : ''}
                    placeholder="Select at least one conversion goal..."
                    data={selectOptions}
                    onChange={handleSelectChange}
                    value={selectValues}
                  />

                  <Text size="sm" c="dimmed">
                    The campaign's conversion goal/s. See{' '}
                    <a
                      href="https://developers.google.com/google-ads/api/videos/catalog/pmax-8"
                      target="_blank"
                      rel="noreferrer"
                    >
                      this link
                    </a>{' '}
                    for details.
                  </Text>
                </>
              )}
            </Grid.Col>
          </Grid>

          <Group justify="flex-end" gap="xs" py="md">
            <Button variant="default" onClick={onBack}>
              {' '}
              Back
            </Button>
            {deleteButton}
            {duplicateButton}
            {showUserFlow && (
              <Button variant="default" onClick={() => showUserFlow()} disabled={!googleAd.name || !googleAd.budget}>
                <IconMail size={14} /> Send
              </Button>
            )}
            <LockableButton
              onClick={() => {
                setSaving(true);
                saveGoogleAd(googleAd)
                  .then(() => {
                    onBack();
                  })
                  .finally(() => {
                    setSaving(false);
                  });
              }}
              restrictionKey={`googleAds${googleAd.id ? 'Update' : 'Create'}`}
              disabled={!googleAd.name || !googleAd.budget || saving}
            >
              <IconDeviceFloppy size={14} /> {saving ? 'Saving...' : 'Save'}
            </LockableButton>
            <ConfirmationModal
              error={deleteError}
              show={confirmDeleteOpen}
              close={() => setConfirmDeleteOpen(false)}
              confirm={(cb) => {
                setDeleteError(undefined);
                deleteGoogleAd(cb).catch(setDeleteError);
              }}
              message={<WarningMessageOnDelete type="Google Ad" />}
            />
          </Group>
        </Box>
      </Card.Section>
    </Card>
  );
};

export default GoogleAdForm;
